// /apps/patient/src/pages/forms/FormResponseList.tsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { FileText, Calendar, Eye, Download, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

interface FormResponseItem {
  id: string;
  form_id: string;
  form_name?: string;
  created_at: number;
  completed_at: number;
  status: 'complete' | 'partial' | 'processed';
  report_id?: string;
}

export function FormResponseList() {
  const navigate = useNavigate();
  const [responses, setResponses] = useState<FormResponseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'complete' | 'partial'>('all');

  useEffect(() => {
    fetchResponses();
  }, []);

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const data = await patientApiService.getMyFormResponses();
      setResponses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching form responses:', error);
      setResponses([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredResponses = responses.filter(response => {
    if (filter === 'all') return true;
    return response.status === filter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
      case 'processed':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mb-6" />
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-100 rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Form Submissions</h1>
        <p className="mt-1 text-sm text-gray-600">
          View your submitted forms and generated reports
        </p>
      </div>

      {/* Filter tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            {['all', 'complete', 'partial'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status as any)}
                className={`
                  py-2 px-4 border-b-2 font-medium text-sm capitalize
                  ${filter === status
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {status}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Responses list */}
      <div className="bg-white rounded-lg shadow-sm">
        {filteredResponses.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredResponses.map((response) => (
              <div
                key={response.id}
                className="p-6 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/forms/response/${response.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-gray-900">
                        {response.form_name || 'Form Submission'}
                      </h3>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {format(response.completed_at || response.created_at, 'MMM d, yyyy')}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(response.status)}`}>
                          {response.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {response.report_id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Handle report download
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Download Report"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    )}
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No form submissions found</p>
          </div>
        )}
      </div>
    </div>
  );
}